import React, { useState, useCallback, useEffect } from 'react';
import ReactFlow, { 
  MiniMap, 
  Controls, 
  Background, 
  ReactFlowProvider,
  applyNodeChanges,
  applyEdgeChanges
} from 'reactflow';
import 'reactflow/dist/style.css';
import { themeStyles, customStyles } from './ArtemisStyles';
import { nodeTypes } from './ArtemisCustomNodes';

// App Builder nodes - showing evolution of applications
const getInitialNodes = () => {
  const savedPositions = localStorage.getItem('artemisAppBuilderPositions');
  if (savedPositions) {
    return JSON.parse(savedPositions);
  }
  
  return [
    // User Intent
    {
      id: 'user-intent',
      data: { label: '💬 "Build a dashboard that gives insights about sales data"' },
      position: { x: 400, y: 50 },
      style: themeStyles.problemNode,
    },
    
    // Dataset Analysis
    {
      id: 'dataset-analysis',
      data: { label: '📊 Dataset Analysis\n• Sales records: 50K rows\n• Time series data\n• Product categories\n• Regional data' },
      position: { x: 800, y: 50 },
      style: { ...themeStyles.analysisNode, width: 220 },
    },
    
    // Initial Code Generation
    {
      id: 'initial-code',
      type: 'collapsibleCode',
      data: { 
        filename: 'dashboard_v1.py',
        code: '# Basic Dashboard\n' +
              'import streamlit as st\n' +
              'import pandas as pd\n' +
              '\n' +
              'st.title("Sales Dashboard")\n' +
              'df = pd.read_csv("sales_data.csv")\n' +
              'st.metric("Total Sales", f"${df[\'sales\'].sum():,.0f}")',
        issues: 'Limited insights, no interactivity, basic visualizations',
        score: 2.5
      },
      position: { x: 400, y: 200 },
    },
    
    // Intent-Based Scoring
    {
      id: 'scoring-label',
      data: { label: '🎯 Intent-Based Scoring System' },
      position: { x: 1100, y: 200 },
      style: { ...themeStyles.metricNode, width: 240 },
    },
    {
      id: 'insight-score',
      data: { label: '💡 Data Insights\nQuality & Depth' },
      position: { x: 950, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'viz-score',
      data: { label: '📊 Visualizations\nClarity & Impact' },
      position: { x: 1100, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'interact-score',
      data: { label: '🎛️ Interactivity\nUser Control' },
      position: { x: 1250, y: 280 },
      style: themeStyles.metricNode,
    },
    {
      id: 'exec-feedback',
      data: { label: '⚡ Execution Logs\n✅ App runs\n⚠️ Slow load: 3.2s' },
      position: { x: 1025, y: 380 },
      style: themeStyles.suggestionNode,
    },
    {
      id: 'human-feedback',
      data: { label: '👤 Human Review\n"Need forecasting"\nScore: 3/5' },
      position: { x: 1175, y: 380 },
      style: themeStyles.suggestionNode,
    },
    
    // Genetic Algorithm Concepts
    {
      id: 'genetic-algo',
      data: { label: '🧬 Genetic Algorithm Engine\n\n🔄 Mutation: Vary UI patterns & features\n🔗 Crossover: Combine best components\n📈 Selection: Keep high-scoring apps\n🎲 Population: 20 app variants' },
      position: { x: 100, y: 500 },
      style: { ...themeStyles.uniqueFeatureNode, width: 300, height: 140 },
    },
    
    // AI Agents
    {
      id: 'ai-agents',
      data: { label: '🤖 Specialized App Building Agents' },
      position: { x: 500, y: 500 },
      style: { ...themeStyles.analysisNode, width: 260 },
    },
    {
      id: 'data-analyst',
      data: { label: '📊 Data Analyst Agent\n\n📝 Prompt:\n"Analyze the dataset and\ncreate meaningful KPIs,\ntrends, and insights\nfor business users"' },
      position: { x: 200, y: 600 },
      style: { ...themeStyles.llmNode, width: 200, height: 140 },
    },
    {
      id: 'viz-expert',
      data: { label: '🎨 Viz Expert Agent\n\n📝 Prompt:\n"Design interactive\nvisualizations that\nclearly communicate\ndata patterns"' },
      position: { x: 500, y: 600 },
      style: { ...themeStyles.llmNode, width: 200, height: 140 },
    },
    {
      id: 'ml-engineer',
      data: { label: '🧠 ML Engineer Agent\n\n📝 Prompt:\n"Add predictive analytics\nand ML-powered insights\nto enhance decision\nmaking"' },
      position: { x: 800, y: 600 },
      style: { ...themeStyles.llmNode, width: 200, height: 140 },
    },
    
    // Generation 1
    {
      id: 'gen1-label',
      data: { label: '🌱 Generation 1: Enhanced Dashboards' },
      position: { x: 500, y: 800 },
      style: { ...themeStyles.metricNode, width: 300 },
    },
    {
      id: 'gen1-1',
      type: 'collapsibleCode',
      data: { 
        filename: 'dashboard_analytics.py (Variant A)',
        code: '# Enhanced Analytics Dashboard\n' +
              '+ import plotly.express as px\n' +
              '+ from datetime import datetime, timedelta\n' +
              '+ \n' +
              '+ # Sidebar filters\n' +
              '+ date_range = st.date_input("Date Range", [])\n' +
              '+ category = st.multiselect("Categories", categories)\n' +
              '+ \n' +
              '+ # Advanced metrics with trends\n' +
              '+ col1, col2, col3 = st.columns(3)\n' +
              '+ col1.metric("Revenue", revenue, delta=f"{growth}%")\n' +
              '+ \n' +
              '+ # Interactive charts\n' +
              '+ fig = px.scatter(df, x="price", y="quantity",\n' +
              '+                 size="revenue", color="category")\n' +
              '+ st.plotly_chart(fig)',
        score: 3.8
      },
      position: { x: 50, y: 900 },
    },
    {
      id: 'gen1-2',
      type: 'collapsibleCode',
      data: { 
        filename: 'dashboard_realtime.py (Variant B)',
        code: '# Real-time Dashboard\n' +
              '+ from streamlit_autorefresh import st_autorefresh\n' +
              '+ import asyncio\n' +
              '+ \n' +
              '+ # Auto refresh every 5 seconds\n' +
              '+ st_autorefresh(interval=5000)\n' +
              '+ \n' +
              '+ # Live KPIs with animations\n' +
              '+ placeholder = st.empty()\n' +
              '+ with placeholder.container():\n' +
              '+     display_live_metrics()\n' +
              '+ \n' +
              '+ # Streaming chart\n' +
              '+ chart = st.line_chart(get_live_data())\n' +
              '+ chart.add_rows(new_data)',
        score: 3.9
      },
      position: { x: 400, y: 900 },
    },
    {
      id: 'gen1-3',
      type: 'collapsibleCode',
      data: { 
        filename: 'dashboard_ml.py (Variant C)',
        code: '# ML-Enhanced Dashboard\n' +
              '+ from prophet import Prophet\n' +
              '+ from sklearn.cluster import KMeans\n' +
              '+ \n' +
              '+ # Forecasting\n' +
              '+ model = Prophet()\n' +
              '+ forecast = model.fit(df).predict(future)\n' +
              '+ \n' +
              '+ # Customer segmentation\n' +
              '+ segments = KMeans(n_clusters=4)\n' +
              '+ df["segment"] = segments.fit_predict(features)\n' +
              '+ \n' +
              '+ # Display predictions\n' +
              '+ st.plotly_chart(plot_forecast(forecast))',
        score: 4.2
      },
      position: { x: 750, y: 900 },
    },
    
    // Evolution Process
    {
      id: 'evolution-1',
      type: 'evolution',
      data: { 
        generation: '1→2',
        description: '🔄 Mutation & Crossover Applied',
        scores: {
          'Insights': '4.2/5',
          'Interactivity': '4.0/5',
          'ML Integration': '3.8/5'
        }
      },
      position: { x: 400, y: 1100 },
    },
    
    // Generation 2
    {
      id: 'gen2-label',
      data: { label: '🧬 Generation 2: Intelligent Dashboards' },
      position: { x: 500, y: 1250 },
      style: { ...themeStyles.metricNode, width: 300 },
    },
    {
      id: 'gen2-1',
      type: 'collapsibleCode',
      data: { 
        filename: 'dashboard_ai_insights.py (Elite)',
        code: '# AI-Powered Insights Dashboard\n' +
              '+ import streamlit as st\n' +
              '+ from transformers import pipeline\n' +
              '+ \n' +
              '+ # Combines best features from Gen 1:\n' +
              '+ # - Interactive filters (Variant A)\n' +
              '+ # - Real-time updates (Variant B)  \n' +
              '+ # - ML predictions (Variant C)\n' +
              '+ \n' +
              '+ # NEW: AI-generated insights\n' +
              '+ insights = generate_ai_insights(df)\n' +
              '+ st.info(f"💡 {insights[0]}")\n' +
              '+ \n' +
              '+ # Anomaly detection alerts\n' +
              '+ if detect_anomalies(latest_data):\n' +
              '+     st.warning("⚠️ Unusual pattern detected!")',
        score: 4.6
      },
      position: { x: 200, y: 1350 },
    },
    {
      id: 'gen2-2',
      type: 'collapsibleCode',
      data: { 
        filename: 'dashboard_integrated.py (Mutated)',
        code: '# Fully Integrated Dashboard\n' +
              '+ # MUTATION: Added collaboration features\n' +
              '+ with st.sidebar:\n' +
              '+     st.write("👥 Active Users:", active_users)\n' +
              '+     comments = st.text_input("💬 Add comment")\n' +
              '+ \n' +
              '+ # Automated report generation\n' +
              '+ if st.button("📥 Generate Report"):\n' +
              '+     report = create_pdf_report(data, insights)\n' +
              '+     st.download_button("Download", report)\n' +
              '+ \n' +
              '+ # API integration for actions\n' +
              '+ if anomaly_detected:\n' +
              '+     send_alert_to_slack(anomaly_details)',
        score: 4.8
      },
      position: { x: 600, y: 1350 },
    },
    
    // Final Evolution
    {
      id: 'final-evolution',
      type: 'evolution',
      data: { 
        generation: 'Final',
        description: '🏆 Production-Ready Dashboard',
        scores: {
          'Data Insights': '4.9/5',
          'Visualizations': '4.8/5',
          'Interactivity': '4.7/5',
          'ML Analysis': '4.8/5',
          'UX Quality': '4.9/5'
        }
      },
      position: { x: 400, y: 1550 },
    },
    
    // Output
    {
      id: 'output',
      data: { label: '✨ Deployed App\nFully Functional Dashboard\nwith AI-Powered Insights' },
      position: { x: 400, y: 1700 },
      style: { ...themeStyles.uniqueFeatureNode, width: 220 },
    }
  ];
};

const appBuilderEdges = [
  // Initial flow
  { id: 'e1', source: 'user-intent', target: 'dataset-analysis', animated: true, style: themeStyles.edgeStyle },
  { id: 'e2', source: 'dataset-analysis', target: 'initial-code', style: themeStyles.edgeStyle },
  { id: 'e3', source: 'initial-code', target: 'scoring-label', animated: true, style: themeStyles.edgeStyle },
  
  // Scoring connections
  { id: 'e4', source: 'scoring-label', target: 'insight-score', style: themeStyles.edgeStyle },
  { id: 'e5', source: 'scoring-label', target: 'viz-score', style: themeStyles.edgeStyle },
  { id: 'e6', source: 'scoring-label', target: 'interact-score', style: themeStyles.edgeStyle },
  { id: 'e7', source: 'scoring-label', target: 'exec-feedback', style: themeStyles.edgeStyle },
  { id: 'e8', source: 'scoring-label', target: 'human-feedback', style: themeStyles.edgeStyle },
  
  // Genetic Algorithm
  { id: 'e9', source: 'initial-code', target: 'genetic-algo', style: themeStyles.evolutionEdgeStyle },
  { id: 'e10', source: 'genetic-algo', target: 'ai-agents', animated: true, style: themeStyles.evolutionEdgeStyle },
  
  // AI Agents
  { id: 'e11', source: 'ai-agents', target: 'data-analyst', style: themeStyles.edgeStyle },
  { id: 'e12', source: 'ai-agents', target: 'viz-expert', style: themeStyles.edgeStyle },
  { id: 'e13', source: 'ai-agents', target: 'ml-engineer', style: themeStyles.edgeStyle },
  
  // Generation 1
  { id: 'e14', source: 'data-analyst', target: 'gen1-1', style: themeStyles.edgeStyle },
  { id: 'e15', source: 'viz-expert', target: 'gen1-2', style: themeStyles.edgeStyle },
  { id: 'e16', source: 'ml-engineer', target: 'gen1-3', style: themeStyles.edgeStyle },
  
  // Evolution
  { id: 'e17', source: 'gen1-1', target: 'evolution-1', style: themeStyles.evolutionEdgeStyle },
  { id: 'e18', source: 'gen1-2', target: 'evolution-1', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e19', source: 'gen1-3', target: 'evolution-1', style: themeStyles.evolutionEdgeStyle },
  
  // Feedback from scoring
  { id: 'f1', source: 'insight-score', target: 'evolution-1', style: themeStyles.feedbackEdgeStyle, type: 'smoothstep' },
  { id: 'f2', source: 'exec-feedback', target: 'evolution-1', style: themeStyles.feedbackEdgeStyle, type: 'smoothstep' },
  { id: 'f3', source: 'human-feedback', target: 'evolution-1', style: themeStyles.feedbackEdgeStyle, type: 'smoothstep' },
  
  // Generation 2
  { id: 'e20', source: 'evolution-1', target: 'gen2-1', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e21', source: 'evolution-1', target: 'gen2-2', animated: true, style: themeStyles.evolutionEdgeStyle },
  
  // Final
  { id: 'e22', source: 'gen2-1', target: 'final-evolution', style: themeStyles.edgeStyle },
  { id: 'e23', source: 'gen2-2', target: 'final-evolution', animated: true, style: themeStyles.edgeStyle },
  { id: 'e24', source: 'final-evolution', target: 'output', animated: true, style: themeStyles.evolutionEdgeStyle },
];

const ArtemisAppBuilder = () => {
  const [nodes, setNodes] = useState(getInitialNodes());
  const [edges, setEdges] = useState(appBuilderEdges);

  // Add custom styles on mount
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = customStyles;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Save positions whenever nodes change
  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => {
      const updatedNodes = applyNodeChanges(changes, nds);
      const positions = updatedNodes.map(node => ({
        ...node,
        style: node.style,
        type: node.type,
        data: node.data
      }));
      localStorage.setItem('artemisAppBuilderPositions', JSON.stringify(positions));
      return updatedNodes;
    });
  }, []);

  const onEdgesChange = useCallback((changes) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);

  // Reset positions
  const resetPositions = () => {
    localStorage.removeItem('artemisAppBuilderPositions');
    setNodes(getInitialNodes());
  };

  return (
    <ReactFlowProvider>
      <div style={{ 
        height: '100vh', 
        width: '100%', 
        background: 'linear-gradient(135deg, #F0F4F8 0%, #D9E2EC 100%)',
        position: 'relative'
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          color: '#102A43', 
          padding: '20px', 
          margin: '0',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          fontSize: '26px',
          fontWeight: '700'
        }}>
          🏗️ Artemis App Builder - Evolving Applications
        </h2>
        
        <div style={{
          position: 'absolute',
          top: '70px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '8px 20px',
          background: 'rgba(16, 42, 67, 0.08)',
          borderRadius: '20px',
          color: '#486581',
          fontSize: '14px',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          zIndex: 100
        }}>
          Watch how AI evolves a simple dashboard into an intelligent, insight-driven application
        </div>

        {/* Reset button */}
        <button
          onClick={resetPositions}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            padding: '8px 16px',
            background: '#627D98',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
            cursor: 'pointer',
            zIndex: 100,
            transition: 'background 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.background = '#486581'}
          onMouseLeave={(e) => e.target.style.background = '#627D98'}
        >
          Reset Layout
        </button>
        
        <div style={{ 
          position: 'absolute',
          top: '120px',
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: 'calc(100% - 120px)'
        }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{
              padding: 0.1,
              minZoom: 0.3,
              maxZoom: 1.0
            }}
            minZoom={0.2}
            maxZoom={1.5}
            defaultViewport={{ x: 0, y: 0, zoom: 0.5 }}
            nodesDraggable={true}
            nodesConnectable={false}
            elementsSelectable={true}
            panOnDrag={true}
            panOnScroll={true}
            zoomOnScroll={true}
            zoomOnPinch={true}
          >
            <MiniMap 
              style={{
                height: 120,
                width: 180,
                backgroundColor: 'rgba(255,255,255,0.8)',
                border: '1px solid #D9E2EC'
              }}
              nodeColor={(node) => {
                return node.style?.background || '#627D98';
              }}
              nodeStrokeColor={() => '#486581'}
              nodeBorderRadius={2}
            />
            <Controls />
            <Background 
              variant="dots" 
              color="#D9E2EC" 
              gap={20} 
              size={1} 
            />
          </ReactFlow>
        </div>

        {/* Key Insights */}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          background: 'rgba(255,255,255,0.95)',
          padding: '15px',
          borderRadius: '15px',
          border: '1px solid #D9E2EC',
          color: '#102A43',
          fontSize: '13px',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          maxWidth: '320px'
        }}>
          <div style={{ marginBottom: '10px', fontWeight: '600', color: '#5E60CE' }}>
            🎯 Intent-Based Evolution
          </div>
          <div style={{ fontSize: '12px', lineHeight: '1.5', color: '#486581' }}>
            Unlike traditional optimization, App Builder evolves based on 
            <span style={{ color: '#F4511E', fontWeight: '600' }}> how well the code fulfills user intent</span>.
            Each generation improves data insights, visualizations, and user experience through genetic algorithms.
          </div>
          <div style={{ marginTop: '10px', padding: '8px', background: 'rgba(94, 96, 206, 0.1)', borderRadius: '8px' }}>
            <strong>Score Improvement:</strong> 2.5 → 4.8 
            <span style={{ color: '#28A745', fontWeight: '600' }}> (+92%)</span>
          </div>
        </div>
      </div>
    </ReactFlowProvider>
  );
};

export default ArtemisAppBuilder; 